/**
 * Pure helpers for the compose window: recipient parsing/validation, send limits and the
 * reply / reply-all / forward prefill. No DOM, no network, nothing logged.
 */
import { formatAddress, formatFullDate } from './mailFormat';
import type { MailMessageDto, MailSendMode, MailThreadSummaryDto } from './mailTypes';

export const MAX_RECIPIENTS = 50;
export const MAX_SUBJECT_LENGTH = 998;
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;
export const PHI_POLICY_LINE = 'Do not include patient health information (PHI) in email.';

const EMAIL_RE = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;

export function isValidEmail(address: string): boolean {
  return address.length <= 254 && EMAIL_RE.test(address) && !address.includes('..');
}

/**
 * Splits pasted or typed recipient text into entries. Accepts comma, semicolon, newline and whitespace
 * separators, and `Name <addr@host>` forms (the address inside the brackets is kept).
 */
export function parseRecipientInput(raw: string): string[] {
  const out: string[] = [];
  // A quoted display name may itself contain commas ("Doe, Jo" <jo@x.com>); only the address matters.
  const withoutQuotedNames = raw.replace(/"[^"]*"\s*(<[^<>]*>)/g, '$1');
  for (const chunk of withoutQuotedNames.split(/[,;\n\r]+/)) {
    const part = chunk.trim();
    if (!part) continue;
    const bracket = /<([^<>]*)>/.exec(part);
    if (bracket) {
      out.push(bracket[1].trim());
    } else {
      for (const word of part.split(/\s+/)) if (word) out.push(word.replace(/^["']+|["']+$/g, ''));
    }
  }
  return out.filter(Boolean);
}

/** Adds entries to a list, ignoring case-insensitive duplicates. */
export function addRecipients(list: string[], entries: string[]): string[] {
  const seen = new Set(list.map((e) => e.toLowerCase()));
  const next = [...list];
  for (const entry of entries) {
    const key = entry.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      next.push(entry);
    }
  }
  return next;
}

export interface ComposeDraft {
  mailboxId: string;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  attachments: File[];
}

export function totalAttachmentBytes(files: File[]): number {
  return files.reduce((sum, f) => sum + f.size, 0);
}

/** First blocking problem with the draft, or null when it can be sent. */
export function validateDraft(draft: ComposeDraft): string | null {
  const all = [...draft.to, ...draft.cc, ...draft.bcc];
  if (!draft.mailboxId) return 'Choose the mailbox to send from.';
  if (draft.to.length === 0) return 'Add at least one recipient in To.';
  if (all.some((e) => !isValidEmail(e))) return 'Fix or remove the highlighted email addresses.';
  const unique = new Set(all.map((e) => e.toLowerCase()));
  if (unique.size > MAX_RECIPIENTS) {
    return `Too many recipients (${unique.size}). The limit is ${MAX_RECIPIENTS} across To, Cc and Bcc.`;
  }
  if (draft.subject.length > MAX_SUBJECT_LENGTH) {
    return `The subject is too long (${draft.subject.length} of ${MAX_SUBJECT_LENGTH} characters).`;
  }
  if (totalAttachmentBytes(draft.attachments) > MAX_ATTACHMENT_BYTES) {
    return 'Attachments exceed the 25 MB limit.';
  }
  return null;
}

// ---------------------------------------------------------------------------------------------
// Reply / reply all / forward prefill
// ---------------------------------------------------------------------------------------------

export interface ComposeSeed {
  mailboxId: string;
  mode: MailSendMode;
  threadId?: string;
  inReplyToMessageId?: string;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  /** Initial plain-text body (quote / forward header); the editor starts above it. */
  bodyText: string;
}

const REPLY_PREFIX = /^\s*re\s*:/i;
const FORWARD_PREFIX = /^\s*(?:fwd?|fw)\s*:/i;

export function prefixSubject(subject: string, mode: MailSendMode): string {
  const s = subject.trim();
  if (mode === 'reply' || mode === 'replyAll') return REPLY_PREFIX.test(s) ? s : `Re: ${s}`.trim();
  if (mode === 'forward') return FORWARD_PREFIX.test(s) ? s : `Fwd: ${s}`.trim();
  return s;
}

/** `> ` quoting of the original plain text; blank lines become a bare `>`. */
export function quotePlainText(text: string): string {
  const lines = text.replace(/\r\n?/g, '\n').replace(/\s+$/, '').split('\n');
  return lines.map((l) => (l === '' ? '>' : `> ${l}`)).join('\n');
}

const dedupeAddresses = (list: string[]) => addRecipients([], list);

export function emptySeed(mailboxId: string): ComposeSeed {
  return { mailboxId, mode: 'new', to: [], cc: [], bcc: [], subject: '', bodyText: '' };
}

export function buildReplySeed(args: {
  mode: Exclude<MailSendMode, 'new'>;
  thread: MailThreadSummaryDto;
  message: MailMessageDto;
  /** Every address the user owns (their mailboxes), excluded from reply-all recipients. */
  ownAddresses: string[];
  /** Mailbox to send from; defaults to the thread's mailbox. */
  mailboxId?: string;
}): ComposeSeed {
  const { mode, thread, message } = args;
  const own = new Set(args.ownAddresses.map((a) => a.toLowerCase()));
  const notOwn = (a: string) => !own.has(a.toLowerCase());
  const base = {
    mailboxId: args.mailboxId ?? thread.mailboxId,
    mode,
    threadId: thread.id,
    inReplyToMessageId: message.id,
    bcc: [] as string[],
    subject: prefixSubject(thread.subject, mode),
  };
  const when = formatFullDate(message.receivedAt ?? message.sentAt);
  const original = message.text ?? '';

  if (mode === 'forward') {
    const header = [
      '---------- Forwarded message ---------',
      `From: ${formatAddress(message.from)}`,
      ...(when ? [`Date: ${when}`] : []),
      `Subject: ${thread.subject}`,
      ...(message.to.length ? [`To: ${message.to.map(formatAddress).join(', ')}`] : []),
      ...(message.cc.length ? [`Cc: ${message.cc.map(formatAddress).join(', ')}`] : []),
    ].join('\n');
    return { ...base, to: [], cc: [], bodyText: `\n\n${header}\n\n${original.replace(/\s+$/, '')}` };
  }

  const wrote = `On ${when ? `${when}, ` : ''}${formatAddress(message.from)} wrote:`;
  const bodyText = `\n\n${wrote}\n${quotePlainText(original)}`;

  if (mode === 'reply') {
    // Answering a message we sent goes to its recipients; otherwise back to the sender.
    const to = message.direction === 'outbound' ? message.to.map((a) => a.address) : [message.from.address];
    return { ...base, to: dedupeAddresses(to), cc: [], bodyText };
  }

  const to = dedupeAddresses([message.from.address, ...message.to.map((a) => a.address)].filter(notOwn));
  const toKeys = new Set(to.map((a) => a.toLowerCase()));
  const cc = dedupeAddresses(message.cc.map((a) => a.address).filter(notOwn)).filter(
    (a) => !toKeys.has(a.toLowerCase()),
  );
  return { ...base, to, cc, bodyText };
}
